<template>
  <div class="landing-page">
    <header class="lp-header">
      <div class="lp-container header-inner">
          <div class="brand">
            <img :src="brandLogo" alt="MOH" class="brand-mark"/>
            <div class="brand-name">iHRIS-HRD</div>
          </div>

        <nav class="nav-links">
          <button
            v-for="link in navLinks"
            :key="link.target"
            class="link-button"
            :class="{'is-active': activeSection === link.target}"
            @click="scrollTo(link.target)"
          >
            {{ link.label }}
          </button>
        </nav>

        <button class="menu-button" @click="mobileNavOpen = true">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <transition name="fade">
        <div v-if="mobileNavOpen" class="mobile-overlay" @click.self="mobileNavOpen = false">
          <div class="mobile-sheet">
            <div class="mobile-header">
              <div class="brand">
                <img :src="brandLogo" alt="MOH" class="brand-mark"/>
                <div class="brand-name">iHRIS-HRD</div>
              </div>
              <button class="close-button" @click="mobileNavOpen = false">X</button>
            </div>
            <nav class="mobile-links">
              <button
                v-for="link in navLinks"
                :key="link.target"
                class="mobile-link"
                @click="handleMobileNav(link.target)"
              >
                {{ link.label }}
              </button>
            </nav>
          </div>
        </div>
      </transition>
    </header>

    <main>
      <section id="hero" class="lp-section hero">
        <div class="lp-container hero-grid">
          <div class="hero-copy">
            <p class="eyebrow">Human Resource Development</p>
            <h1 class="hero-title">
              iHRIS-HRD<br>
              <span class="text-accent">Human Resource Development</span>
            </h1>
            <p class="lead">
              Manage graduates, short and long term trainings, programs, trainees, and trainers in one place. With each
              record tied to the person and program, you can see workforce capacity at any time—without digging through
              spreadsheets.
            </p>
            <div class="hero-actions">
              <button class="btn primary" @click="goToLogin">Sign in</button>
              <button
                class="btn elms-hero-btn"
                :class="{ 'elms-hero-btn--active': showElmsGuide }"
                :aria-expanded="showElmsGuide ? 'true' : 'false'"
                aria-controls="elms-guide"
                @click="toggleElmsGuide"
              >
                eLMS integrations
              </button>
            </div>
            <p class="tiny-note">
              Built for MOH teams that prioritize accurate HR data, clear workflows, and actionable development insights.
            </p>
          </div>

        </div>
      </section>

      <section id="insights" class="lp-section alt">
        <div class="lp-container">
          <div class="insights-header">
            <div>
              <p class="eyebrow muted">Public snapshot</p>
              <h2 class="section-title">HRD momentum at a glance</h2>
              <p class="section-lead narrow">
                High-level workforce and training signals surfaced for decision makers—kept simple for a quick read.
              </p>
            </div>
          </div>

          <div class="insight-wrap">
            <div class="insight-mini-grid public-kpi-grid">
              <div v-for="metric in publicKpis" :key="metric.title" class="mini-card public-kpi-card">
                <div class="mini-top">
                  <span class="mini-title">{{ metric.title }}</span>
                </div>
                <div class="mini-value">{{ formatNumber(metric.value) }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="product" class="lp-section">
        <div class="lp-container">
          <p class="eyebrow muted">Products</p>
          <h2 class="section-title">Human Resource Development (HRD)</h2>
          <p class="section-lead">
            Designed to manage the health sector workforce across all levels of the health system—Ministry, Regional,
            Hospital, Zonal, and Woreda—this system enables decision-makers to access reliable evidence for informed
            policy and staffing decisions. It supports comprehensive management of graduates, short- and long-term
            trainings, programs, trainees, and trainers, ensuring training records remain linked to the appropriate
            individuals and facilities. As a result, workforce capacity and training coverage can be easily tracked and
            understood at a glance.
          </p>
          <div class="card-grid three-up">
            <div
              v-for="highlight in productHighlights"
              :key="highlight.title"
              class="info-card"
            >
              <h3>{{ highlight.title }}</h3>
              <p>{{ highlight.body }}</p>
            </div>
          </div>

          <div class="timeline">
            <div class="timeline-header">
              <p class="eyebrow muted">Rollout path</p>
              <p class="timeline-caption">Built to follow existing HR workflows, step by step.</p>
            </div>
            <div class="timeline-steps">
              <div
                v-for="step in steps"
                :key="step.title"
                class="timeline-step"
              >
                <div class="timeline-badge">{{ step.step }}</div>
                <div>
                  <div class="timeline-title">{{ step.title }}</div>
                  <div class="timeline-text">{{ step.body }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="hr-roles" class="lp-section">
        <div class="lp-container">
          <p class="eyebrow muted">For every HR lead</p>
          <h2 class="section-title">Built for the whole HR ecosystem</h2>
          <p class="section-lead narrow">
            iHRIS-HRD supports ministry, regional, hospital, zonal, and woreda teams with one consistent, simple view of the workforce.
          </p>
          <div class="card-grid four-up">
            <div
              v-for="audience in clinicians"
              :key="audience.title"
              class="info-card"
            >
              <h3>{{ audience.title }}</h3>
              <p>{{ audience.body }}</p>
            </div>
          </div>
        </div>
      </section>

    </main>

    <footer class="lp-footer">
      <div class="lp-container footer-content">
        <div class="footer-meta">
          <div>(c) {{ currentYear }} MOH iHRIS-HRD.</div>
          <div class="footer-subtitle">Simple. Human. Stewarded by the Ministry of Health.</div>
        </div>
        <div class="footer-links">
          <div class="footer-link" v-for="link in footerLinks" :key="link.label">
            <a :href="link.href">{{ link.label }}</a>
          </div>
          <div class="footer-subtitle">Need to talk compliance? Email us anytime.</div>
        </div>
      </div>
    </footer>

    <transition name="fade-slide">
      <div v-if="showElmsGuide" class="elms-modal" role="dialog" aria-modal="true" aria-labelledby="elms-guide-title">
        <div class="elms-modal__backdrop" @click="closeElmsGuide"></div>
        <div class="elms-modal__content" role="document">
          <div id="elms-guide" class="elms-guide">
            <div class="elms-guide-header">
              <div class="elms-guide-title" id="elms-guide-title">eLMS Integrations Developer Guideline</div>
              <button class="elms-close" type="button" aria-label="Close" @click="closeElmsGuide">×</button>
              <div class="elms-guide-subtitle">
                Follow these steps to connect your e-learning tool to iHRIS-HTD.
              </div>
            </div>
            <div class="elms-guide-body">
              <ol class="elms-steps">
                <li>
                  <div class="elms-step-title">Register the E-learning Tool</div>
                  <div class="elms-step-text">
                    Ensure the e-learning tool is registered as a training center in the iHRIS-HTD system.
                  </div>
                  <transition name="fade-slide">
                    <div v-if="showCpdForm" class="elms-form-card">
                      <div v-if="cpdSubmitted" class="elms-wait">
                        Submitted. Please wait until approval by MOH team.
                      </div>
                      <form v-else class="elms-form" @submit.prevent="submitCpdForm">
                        <div class="elms-form-grid">
                          <label class="elms-field">
                            Training center name
                            <input v-model="cpdForm.trainingCenterName" class="elms-input" type="text" required>
                          </label>
                          <label class="elms-field">
                            Contact person
                            <input v-model="cpdForm.contactPerson" class="elms-input" type="text" required>
                          </label>
                          <label class="elms-field">
                            Email
                            <input v-model="cpdForm.email" class="elms-input" type="email" required>
                          </label>
                          <label class="elms-field">
                            Phone
                            <input v-model="cpdForm.phone" class="elms-input" type="tel" required>
                          </label>
                          <label class="elms-field">
                            Region
                            <input v-model="cpdForm.region" class="elms-input" type="text" required>
                          </label>
                        </div>
                        <div class="elms-form-actions">
                          <button class="btn elms-submit-btn" type="submit">Submit registration</button>
                        </div>
                      </form>
                    </div>
                  </transition>
                </li>
                <li>
                  <div class="elms-step-title">Get credential for integration</div>
                  <div class="elms-step-text">
                    After approval, request the integration credentials from the MOH team to access the APIs securely.
                  </div>
                </li>
                <li>
                  <div class="elms-step-title">Register the Course</div>
                  <div class="elms-step-text">
                    The course offered in the e-learning tool must be registered in the iHRIS system under its respective details.
                  </div>
                  <transition name="fade-slide">
                    <div v-if="showCourseForm" class="elms-form-card">
                      <div v-if="courseSubmitted" class="elms-wait">
                        Submitted. Please wait until approval by MOH team.
                      </div>
                      <form v-else class="elms-form" @submit.prevent="submitCourseForm">
                        <div class="elms-form-grid">
                          <label class="elms-field">
                            Course title
                            <input v-model="courseForm.title" class="elms-input" type="text" required>
                          </label>
                          <label class="elms-field">
                            Course code
                            <input v-model="courseForm.code" class="elms-input" type="text" required>
                          </label>
                          <label class="elms-field">
                            Course level
                            <input v-model="courseForm.level" class="elms-input" type="text">
                          </label>
                          <label class="elms-field">
                            Duration (hours)
                            <input v-model="courseForm.duration" class="elms-input" type="number" min="1">
                          </label>
                          <label class="elms-field elms-field--full">
                            Description
                            <textarea v-model="courseForm.description" class="elms-input" rows="3"></textarea>
                          </label>
                        </div>
                        <div class="elms-form-actions">
                          <button class="btn elms-submit-btn" type="submit">Submit course</button>
                        </div>
                      </form>
                    </div>
                  </transition>
                </li>
                <li>
                  <div class="elms-step-title">Obtain Authentication Token</div>
                  <div class="elms-step-text">
                    Use the following API to log in and obtain a token for authentication:
                  </div>
                  <div class="elms-api">
                    <span class="elms-method post">POST</span>
                    <code>https://ihris.moh.gov.et/hrd/api/user/login</code>
                    <button
                      class="elms-copy-btn"
                      type="button"
                      aria-label="Copy URL"
                      title="Copy URL"
                      @click="copyToClipboard('https://ihris.moh.gov.et/hrd/api/user/login')"
                    >
                      <svg class="elms-copy-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M16 4H8a2 2 0 0 0-2 2v10h2V6h8V4zm3 4H12a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm0 12h-7V10h7v10z" />
                      </svg>
                    </button>
                  </div>
                </li>
                <li>
                  <div class="elms-step-title">Fetch Course Information</div>
                  <div class="elms-step-text">
                    Retrieve course details to map them with your e-learning tool:
                  </div>
                  <div class="elms-api">
                    <span class="elms-method get">GET</span>
                    <code>https://ihris.moh.gov.et/hrd/api/e-learning/courses</code>
                    <button
                      class="elms-copy-btn"
                      type="button"
                      aria-label="Copy URL"
                      title="Copy URL"
                      @click="copyToClipboard('https://ihris.moh.gov.et/hrd/api/e-learning/courses')"
                    >
                      <svg class="elms-copy-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M16 4H8a2 2 0 0 0-2 2v10h2V6h8V4zm3 4H12a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm0 12h-7V10h7v10z" />
                      </svg>
                    </button>
                  </div>
                  <div class="elms-api">
                    <span class="elms-method get">GET</span>
                    <code>https://ihris.moh.gov.et/hrd/api/e-learning/course-levels</code>
                    <span class="elms-optional">(Optional)</span>
                    <button
                      class="elms-copy-btn"
                      type="button"
                      aria-label="Copy URL"
                      title="Copy URL"
                      @click="copyToClipboard('https://ihris.moh.gov.et/hrd/api/e-learning/course-levels')"
                    >
                      <svg class="elms-copy-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M16 4H8a2 2 0 0 0-2 2v10h2V6h8V4zm3 4H12a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm0 12h-7V10h7v10z" />
                      </svg>
                    </button>
                  </div>
                </li>
                <li>
                  <div class="elms-step-title">Send Completed Trainee Data</div>
                  <div class="elms-step-text">
                    When a trainee completes a course and passes, send their details via this API:
                  </div>
                  <div class="elms-api">
                    <span class="elms-method post">POST</span>
                    <code>https://ihris.moh.gov.et/hrd/api/trainee/complete-courses</code>
                    <button
                      class="elms-copy-btn"
                      type="button"
                      aria-label="Copy URL"
                      title="Copy URL"
                      @click="copyToClipboard('https://ihris.moh.gov.et/hrd/api/trainee/complete-courses')"
                    >
                      <svg class="elms-copy-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M16 4H8a2 2 0 0 0-2 2v10h2V6h8V4zm3 4H12a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm0 12h-7V10h7v10z" />
                      </svg>
                    </button>
                  </div>
                  <div class="elms-code-label">
                    Payload Example
                    <button
                      class="elms-copy-btn"
                      type="button"
                      aria-label="Copy payload"
                      title="Copy payload"
                      @click="copyToClipboard(payloadExample)"
                    >
                      <svg class="elms-copy-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M16 4H8a2 2 0 0 0-2 2v10h2V6h8V4zm3 4H12a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm0 12h-7V10h7v10z" />
                      </svg>
                    </button>
                  </div>
                  <pre class="elms-code"><code>{{ payloadExample }}</code></pre>
                </li>
              </ol>
              <div class="elms-notes">
                <div class="elms-notes-title">Notes</div>
                <ul>
                  <li>region, zone, and woreda are optional but must align with MFR (Master Facility Registry).</li>
                  <li>Ensure courseId corresponds to the registered course.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import ApiService from "@/common/api.service";

export default {
  name: "LandingPage",
  data() {
    return {
      brandLogo: require("@/assets/img/MOH_logo.png"),
      mobileNavOpen: false,
      activeSection: "product",
      navLinks: [
        {label: "Insights", target: "insights"},
        {label: "Product", target: "product"},
        {label: "HR roles", target: "hr-roles"},
      ],
      productHighlights: [
        {
          title: "Complete development records",
          body: "Manage graduates, training programs, trainees, and trainers with structured data entry and review.",
        },
        {
          title: "Training oversight",
          body: "Track short and long term training participation, attendance, and certification status in real time.",
        },
      ],
      showElmsGuide: false,
      showCpdForm: false,
      showCourseForm: false,
      cpdSubmitted: false,
      courseSubmitted: false,
      cpdForm: {
        trainingCenterName: "",
        contactPerson: "",
        email: "",
        phone: "",
        region: "",
      },
      courseForm: {
        title: "",
        code: "",
        level: "",
        duration: "",
        description: "",
      },
      payloadExample: `{
  "trainee": {
    "prefix": "Ato",
    "firstName": "string",
    "middleName": "string",
    "lastName": "string",
    "gender": "Male",
    "profession": "string",
    "region": "string",
    "zone": "string",
    "woreda": "string",
    "email": "user@example.com",
    "phone": "0761229153",
    "employeeId": "string",
    "licenseNumber": "string",
    "nationalId": "string"
  },
  "startDate": "2024-09-10",
  "score": 100,
  "courseId": 0
}`,
      publicKpis: [
        {
          title: "Total No. of Health Professionals",
          value: 0,
          color: "#0f172a",
          keys: [
            "totalHealthProfessionals",
            "totalNoOfHealthProfessionals",
            "healthProfessionalsTotal",
            "total_health_professionals",
            "totalHealthProfessional",
          ],
        },
        {
          title: "Trainees Under CPD Activity",
          value: 0,
          color: "#0ea5e9",
          keys: [
            "traineesUnderCpdActivity",
            "traineesUnderCPDActivity",
            "trainees_under_cpd_activity",
            "cpdActivityTrainees",
            "totalTraineesUnderCpdActivity",
          ],
        },
        {
          title: "Total Trainees who took CEU",
          value: 0,
          color: "#22c55e",
          keys: [
            "totalTraineesTookCeu",
            "totalTraineesWhoTookCeu",
            "totalTraineesWithCeu",
            "total_trainees_took_ceu",
            "totalTraineesTookCEU",
          ],
        },
        {
          title: "Total Trainees who failed",
          value: 0,
          color: "#f97316",
          keys: [
            "totalTraineesFailed",
            "totalTraineesWhoFailed",
            "total_trainees_failed",
            "traineesFailed",
          ],
        },
        {
          title: "Total Courses taken by trainees",
          value: 0,
          color: "#6366f1",
          keys: [
            "totalCoursesTakenByTrainees",
            "totalCoursesTaken",
            "total_courses_taken_by_trainees",
            "coursesTakenByTrainees",
          ],
        },
      ],
      clinicians: [
        {
          title: "Ministry HR",
          body: "Monitor national HRD indicators and program performance with consistent data from every level.",
        },
        {
          title: "Regional HR",
          body: "Coordinate trainings and track facility readiness with aligned reporting requirements.",
        },
        {
          title: "Facility HR",
          body: "Maintain accurate trainee and trainer records, certifications, and attendance histories.",
        },
        {
          title: "Leadership",
          body: "View aggregated workforce capacity to inform policy, staffing, and investment decisions.",
        },
      ],
      steps: [
        {
          step: "1",
          title: "Collect",
          body: "HR teams capture training, program, and personnel details in a guided, consistent way.",
        },
        {
          step: "2",
          title: "Organize",
          body: "Records are structured automatically so approvals, attendance, and completions stay clear.",
        },
        {
          step: "3",
          title: "Measure",
          body: "Capacity indicators and training coverage reveal where to invest and how to improve performance.",
        },
      ],
      footerLinks: [
        {label: "Privacy", href: "#"},
        {label: "Terms", href: "#"},
        {label: "Contact", href: "mailto:hello@link.health"},
      ],
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },
  methods: {
    formatNumber(value) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return value;
      }
      return numberValue.toLocaleString();
    },
    pickFirstNumber(payload, keys, fallback) {
      if (!payload) {
        return fallback;
      }
      for (const key of keys) {
        const candidate = payload[key];
        if (candidate !== undefined && candidate !== null && candidate !== "") {
          const numberValue = Number(candidate);
          if (!Number.isNaN(numberValue)) {
            return numberValue;
          }
        }
      }
      return fallback;
    },
    async fetchPublicDashboardSummary() {
      try {
        const response = await ApiService.get("/public/dashboard/summary");
        const payload = response && response.data ? (response.data.data || response.data) : {};
        this.publicKpis = this.publicKpis.map((kpi) => ({
          ...kpi,
          value: this.pickFirstNumber(payload, kpi.keys, kpi.value),
        }));
      } catch (error) {
        // Keep fallback values when the public endpoint is unavailable.
      }
    },
    toggleElmsGuide() {
      this.showElmsGuide = !this.showElmsGuide;
    },
    closeElmsGuide() {
      this.showElmsGuide = false;
    },
    toggleCpdForm() {
      this.showCpdForm = !this.showCpdForm;
    },
    toggleCourseForm() {
      this.showCourseForm = !this.showCourseForm;
    },
    submitCpdForm() {
      this.cpdSubmitted = true;
    },
    submitCourseForm() {
      this.courseSubmitted = true;
    },
    async copyToClipboard(text) {
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          return;
        }
      } catch (error) {
        // Fallback when clipboard API is not available.
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },
    scrollTo(target) {
      this.activeSection = target;
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({behavior: "smooth", block: "start"});
      }
    },
    handleMobileNav(target) {
      this.mobileNavOpen = false;
      this.$nextTick(() => this.scrollTo(target));
    },
    goToLogin() {
      this.$router.push("/login");
    },
  },
  created() {
    this.fetchPublicDashboardSummary();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Inter:wght@400;500;600&display=swap");

.landing-page {
  --ink: #0f172a;
  --ink-soft: #0b2940;
  --muted: #6b7280;
  --border: #e5e7eb;
  --bg-soft: #f7f9fb;
  --card: #ffffff;
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  background: #ffffff;
  min-height: 100vh;
}

.lp-container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
}

.lp-header {
  position: sticky;
  top: 0;
  z-index: 40;
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid var(--border);
}

.header-inner {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  justify-self: start;
}

.brand-mark {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.brand-name {
  font-family: "Poppins", "Inter", sans-serif;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 14px;
}

.nav-links {
  display: flex;
  gap: 18px;
  align-items: center;
  justify-self: end;
  justify-content: flex-end;
}

.link-button {
  background: none;
  border: none;
  color: var(--muted);
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  border-bottom: 2px solid transparent;
  padding-bottom: 6px;
}

.link-button:hover {
  color: #0f172a;
  transform: translateY(-1px);
}

.link-button.is-active {
  color: var(--ink);
  border-color: var(--ink);
}

.link-button:focus,
.link-button:focus-visible {
  outline: none;
  box-shadow: none;
}

.cta-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn {
  border-radius: 12px;
  border: 1px solid transparent;
  padding: 10px 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn.primary {
  background: #1976d2;
  color: #fff;
  box-shadow: 0 10px 25px rgba(25, 118, 210, 0.25);
}

.btn.primary:hover {
  background: #1565c0;
}

.btn.ghost {
  background: #fff;
  color: var(--ink);
  border-color: rgba(11, 31, 59, 0.16);
}

.btn.ghost:hover {
  background: rgba(11, 31, 59, 0.04);
}

.btn.outline {
  background: transparent;
  color: var(--ink);
  border-color: rgba(11, 31, 59, 0.2);
}

.menu-button {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 9px;
  cursor: pointer;
  justify-self: end;
}

.menu-button span {
  display: block;
  width: 18px;
  height: 2px;
  background: #111827;
}

.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  justify-content: flex-end;
  z-index: 50;
}

.mobile-sheet {
  width: 90%;
  max-width: 340px;
  background: #fff;
  height: 100%;
  padding: 22px;
  box-shadow: -10px 0 30px rgba(15, 23, 42, 0.15);
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close-button {
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 14px;
  cursor: pointer;
}

.mobile-links {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mobile-link {
  background: none;
  border: none;
  text-align: left;
  font-weight: 600;
  color: #111827;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
}

.mobile-cta {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lp-section {
  padding: 80px 0;
  border-bottom: 1px solid var(--border);
}

.lp-section.alt {
  background: var(--bg-soft);
}

.hero {
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  overflow: hidden;
}

.hero::after {
  content: "";
  position: absolute;
  right: -140px;
  top: -160px;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle at center, rgba(15, 23, 42, 0.12), transparent 60%);
  filter: blur(12px);
}

.hero-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 36px;
  align-items: center;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.eyebrow {
  font-size: 12px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--ink);
  font-weight: 700;
}

.eyebrow.muted {
  color: var(--muted);
}

.hero-title {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 40px;
  line-height: 1.1;
  margin: 0;
}

.text-accent {
  color: var(--ink);
}

.lead {
  font-size: 16px;
  line-height: 1.7;
  color: #4b5563;
  max-width: 640px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tiny-note {
  font-size: 12px;
  color: #6b7280;
  max-width: 360px;
}

.hero-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 24px;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.08);
  padding: 22px;
}

.card-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #0b1f3b;
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 14px;
}

.card-list {
  display: grid;
  gap: 12px;
}

.card-list-item {
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 12px;
  background: #fafbfc;
}

.item-title {
  font-weight: 700;
  margin-bottom: 6px;
  color: #0f172a;
}

.item-body {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
}

.section-title {
  font-family: "Space Grotesk", "Manrope", sans-serif;
  font-size: 28px;
  margin: 10px 0;
  color: #0f172a;
}

.section-lead {
  color: #4b5563;
  font-size: 15px;
  line-height: 1.7;
  max-width: 760px;
}

.section-lead.narrow {
  max-width: 640px;
}

.card-grid {
  margin-top: 26px;
  display: grid;
  gap: 16px;
}

.card-grid.three-up {
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.card-grid.four-up {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.insights-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.elms-hero-btn {
  background: #0b2940;
  color: #ffffff;
  border-color: #0b2940;
  box-shadow: 0 10px 20px rgba(11, 41, 64, 0.2);
}

.elms-hero-btn:hover {
  background: #0f3a5b;
  border-color: #0f3a5b;
  transform: translateY(-1px);
}

.elms-hero-btn--active {
  background: #0f3a5b;
  border-color: #0f3a5b;
  box-shadow: 0 12px 24px rgba(11, 41, 64, 0.26);
}

.elms-inline-btn {
  margin-top: 10px;
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
  padding: 8px 14px;
  font-size: 13px;
}

.elms-inline-btn:hover {
  background: #1f2937;
  border-color: #1f2937;
}

.elms-form-card {
  margin-top: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  background: #f8fafc;
}

.elms-form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.elms-field {
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: #1f2937;
  font-weight: 600;
}

.elms-field--full {
  grid-column: 1 / -1;
}

.elms-input {
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 13px;
  background: #ffffff;
}

.elms-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
}

.elms-form-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.elms-submit-btn {
  background: #1976d2;
  color: #ffffff;
  border-color: #1976d2;
}

.elms-submit-btn:hover {
  background: #1565c0;
  border-color: #1565c0;
}

.elms-wait {
  background: #ecfeff;
  border: 1px solid #bae6fd;
  color: #0f172a;
  padding: 12px 14px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
}

.elms-guide {
  margin: 0;
  border-radius: 18px;
  border: 1px solid var(--border);
  background: #ffffff;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1);
  overflow: hidden;
}

.elms-guide-header {
  position: relative;
  padding: 18px 22px;
  background: linear-gradient(120deg, #eef6ff, #f8fafc 60%);
  border-bottom: 1px solid var(--border);
}

.elms-guide-title {
  font-family: "Space Grotesk", "Manrope", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.elms-guide-subtitle {
  margin-top: 6px;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
}

.elms-guide-body {
  padding: 18px 22px 22px;
}

.elms-steps {
  margin: 0;
  padding-left: 20px;
  color: #0f172a;
}

.elms-steps li {
  margin-bottom: 14px;
}

.elms-step-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.elms-step-text {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
}

.elms-api {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.elms-copy-btn {
  border: 1px solid #cbd5f5;
  background: #eef2ff;
  color: #1e3a8a;
  border-radius: 999px;
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.elms-copy-icon {
  width: 16px;
  height: 16px;
  display: block;
  fill: currentColor;
}

.elms-copy-btn:hover {
  background: #e0e7ff;
}

.elms-method {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.elms-method.post {
  background: #dbeafe;
  color: #1d4ed8;
}

.elms-method.get {
  background: #dcfce7;
  color: #15803d;
}

.elms-api code {
  font-family: "SFMono-Regular", "Consolas", "Liberation Mono", "Menlo", monospace;
  font-size: 12px;
  color: #0f172a;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.elms-optional {
  font-size: 12px;
  color: #64748b;
}

.elms-code-label {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.elms-code {
  margin: 8px 0 0;
  padding: 14px 16px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.6;
  border: 1px solid #1e293b;
  overflow-x: auto;
}

.elms-notes {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--border);
}

.elms-notes-title {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 6px;
}

.elms-notes ul {
  margin: 0;
  padding-left: 18px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.25s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.elms-modal {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.elms-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
}

.elms-modal__content {
  position: relative;
  width: min(980px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  z-index: 1;
}

.elms-close {
  position: absolute;
  top: 14px;
  right: 16px;
  border: none;
  background: #ffffff;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #0f172a;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
}

.elms-close:hover {
  background: #f1f5f9;
}

.insights-pill {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
  border-radius: 999px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.insight-grid {
  margin-top: 18px;
}

.insight-wrap {
  display: grid;
  gap: 16px;
}

.insight-hero {
  background: linear-gradient(135deg, #0f172a, #0b2940);
  color: #e5e7eb;
  border-radius: 18px;
  padding: 18px;
  display: grid;
  gap: 12px;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.16);
}

.insight-hero-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.pill-dark {
  background: rgba(255, 255, 255, 0.12);
  color: #e5e7eb;
  border-radius: 999px;
  padding: 6px 12px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.insight-hero-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
}

.insight-hero-value {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: #ffffff;
}

.insight-hero-delta {
  color: #a5f3fc;
  font-size: 14px;
}

.insight-hero-delta.positive {
  color: #22c55e;
}

.muted-text {
  color: rgba(229, 231, 235, 0.8);
  font-size: 13px;
}

.insight-chart {
  flex: 1;
  min-width: 220px;
  display: grid;
  grid-auto-flow: column;
  align-items: end;
  gap: 6px;
  height: 120px;
}

.insight-chart .chart-bar {
  width: 16px;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.5));
}

.insight-mini-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.public-kpi-grid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

@media (max-width: 1200px) {
  .public-kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
}

.mini-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 12px;
  display: grid;
  gap: 6px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.mini-top {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #1f2937;
}

.mini-title {
  font-size: 13px;
}

.mini-value {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.public-kpi-card .mini-title {
  line-height: 1.35;
  font-size: 14px;
}

.public-kpi-card .mini-value {
  font-size: 26px;
}

.public-kpi-card {
  min-height: 140px;
  padding: 16px;
  text-align: center;
  align-items: center;
}

.public-kpi-card .mini-top {
  justify-content: center;
}

.mini-delta {
  font-size: 12px;
  color: #6b7280;
}

.mini-delta.positive {
  color: #0f9d58;
}

.insight-card {
  gap: 10px;
}

.insight-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #1f2937;
}

.insight-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.insight-value {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.insight-delta {
  font-size: 13px;
  color: #6b7280;
}

.insight-delta.positive {
  color: #0f9d58;
}

.spark-bars {
  display: grid;
  grid-auto-flow: column;
  align-items: end;
  gap: 4px;
  height: 52px;
  margin-top: 6px;
}

.spark-bar {
  width: 10px;
  border-radius: 6px 6px 0 0;
  opacity: 0.9;
}

.info-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
  display: grid;
  gap: 8px;
}

.info-card h3 {
  margin: 0;
  font-size: 17px;
  color: #0f172a;
}

.info-card p {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
  font-size: 14px;
}

.focus-card {
  position: relative;
  padding-top: 22px;
}

.focus-card .color-bar {
  width: 80px;
  height: 6px;
  border-radius: 999px;
  position: absolute;
  top: 10px;
  left: 18px;
}

.step-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: start;
}

.step-index {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: var(--ink);
  color: #fff;
  font-weight: 700;
  display: grid;
  place-items: center;
}

.timeline {
  margin-top: 26px;
  display: grid;
  gap: 12px;
}

.timeline-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.timeline-caption {
  color: #4b5563;
  font-size: 13px;
}

.timeline-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.timeline-step {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: start;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.timeline-badge {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--ink);
  color: #fff;
  font-weight: 700;
  display: grid;
  place-items: center;
  font-size: 14px;
}

.timeline-title {
  font-weight: 700;
  color: #0f172a;
}

.timeline-text {
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
}

.lp-footer {
  background: #ffffff;
  border-top: 1px solid var(--border);
  padding: 24px 0;
  font-size: 13px;
  color: #6b7280;
}

.footer-content {
  display: grid;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.footer-meta {
  display: grid;
  gap: 6px;
}

.footer-subtitle {
  color: #4b5563;
}

.footer-links {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.footer-link a {
  color: #0f172a;
  text-decoration: none;
  font-weight: 600;
}

.footer-link a:hover {
  color: var(--ink);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 1024px) {
  .nav-links,
  .cta-group {
    display: none;
  }

  .menu-button {
    display: inline-flex;
  }

  .header-inner {
    padding: 14px 0;
  }
}

@media (max-width: 720px) {
  .lp-section {
    padding: 60px 0;
  }

  .elms-hero-btn {
    width: 100%;
  }

  .hero-title {
    font-size: 32px;
  }

  .hero-card {
    border-radius: 18px;
  }
}
</style>
